import { maskedValuesForDisplay } from 'src/utils/parameterMasking';
import {
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useState } from 'react';

import { formatJsonForDisplay } from 'src/utils/jsonUtil';

// Best-effort "raw" representation: if the value is already a string,
// preserve it byte-for-byte (that's the actual on-wire shape). For
// objects, stringify compactly. Returns null for empty inputs so the
// caller can fall back to the formatted view's placeholder.
const formatRawForDisplay = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/**
 * Read-only pretty-printed JSON viewer used by the sync / audit log
 * collapse panels.
 *
 * Two display modes:
 *   * `formatted` (default) — recursively decodes nested JSON-encoded
 *     strings (sync log payloads come back double-encoded — see
 *     `deepParseJson`) and indents the result.
 *   * `raw` — shows the value verbatim, so backend engineers can see the
 *     exact on-wire bytes (including escape sequences) when diagnosing
 *     serialization issues.
 *
 * Both views are copyable; copy uses whichever mode is currently shown.
 * Renders `∅` when the value is empty so the slot stays visible (rather
 * than collapsing) — useful when only one of Request / Response is
 * populated.
 */
const JsonBlock = ({ title, value, sensitive = false }) => {
  const [mode, setMode] = useState('formatted');
  const [copied, setCopied] = useState(false);

  const formatted = formatJsonForDisplay(maskedValuesForDisplay(value, sensitive));
  const raw = formatRawForDisplay(maskedValuesForDisplay(value, sensitive));
  const isEmpty = formatted == null && raw == null;

  // Fall back to raw when the value isn't parseable JSON — saves the
  // user a click when the formatted view would just show the same
  // string. Falls back to formatted when raw is empty (e.g. object
  // input).
  const activeMode = formatted == null ? 'raw' : raw == null ? 'formatted' : mode;
  const activeContent = activeMode === 'raw' ? raw : formatted;
  const canToggle = formatted != null && raw != null && formatted !== raw;

  const handleCopy = async () => {
    if (isEmpty) return;
    try {
      await navigator.clipboard.writeText(activeContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail in non-secure contexts; silently ignore.
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}
        >
          {title}
        </Typography>
        {!isEmpty && (
          <Stack direction="row" spacing={1} alignItems="center">
            {canToggle && (
              <ToggleButtonGroup
                value={activeMode}
                exclusive
                size="small"
                onChange={(_, next) => {
                  if (next) setMode(next);
                }}
                aria-label="View mode"
                sx={{
                  '& .MuiToggleButton-root': {
                    py: 0.25,
                    px: 1,
                    fontSize: 10,
                    lineHeight: 1.2,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    fontWeight: 600,
                  },
                }}
              >
                <ToggleButton value="formatted">Formatted</ToggleButton>
                <ToggleButton value="raw">Raw</ToggleButton>
              </ToggleButtonGroup>
            )}
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton size="small" onClick={handleCopy} aria-label="Copy">
                {copied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: 'background.default',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          fontSize: 12,
          lineHeight: 1.55,
          fontFamily: 'monospace',
          maxHeight: 320,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: isEmpty ? 'text.disabled' : 'text.primary',
        }}
      >
        {isEmpty ? '∅' : activeContent}
      </Box>
    </Box>
  );
};

export default JsonBlock;
